import classNames from "classnames";
import React from "react";
import KuikaButton from "../kuika-button/kuika-button";
import KuikaOverflowText from "../kuika-overflow-text/kuika-overflow-text";
import { OVERFLOW_REVEAL_HOST_CLASS } from "../kuika-overflow-text/overflow-reveal-controller";
import KuikaSvg from "../kuika-svg/kuika-svg";
import KuikaTooltip from "../kuika-tooltip/kuika-tooltip";
import KuikaTextMedium from "../typography/TextMedium/TextMedium";
import "./kuika-editor-title.scss";

/**
 * What an editor's top bar says it is editing, and the one control that changes it.
 *
 * Every tool had grown its own copy of this: raw inline SVGs with baked-in hexes in the
 * data-diagram headers, antd's `EditOutlined`/`CheckOutlined` in the vision builder, ui-kit
 * icon buttons in Scheduled Jobs. They were the same three buttons around the same field,
 * so they live here once.
 *
 * Presentation only. The field itself stays with the host — it is an antd `Form.Item` bound
 * to the editor's own form, with the host's uniqueness and format rules — because a field
 * lifted out of its `<Form>` binds to nothing.
 */

export interface KuikaEditorTitleProps {
  /** What the record IS: a `KuikaEntitySwitcher` chip, or a `KuikaEditorIdentity`. */
  identity: React.ReactNode;
  /** Sits after the name: a process-type badge, a draft chip — never a commit control. */
  trailing?: React.ReactNode;
  isRenaming?: boolean;
  /** The host's bound field. Replaces the identity while renaming. */
  renameField?: React.ReactNode;
  /** False where the tool owns the name — a record made from a template. */
  canRename?: boolean;
  /** False while the record has no name to fall back to, so abort would strand it. */
  canAbortRename?: boolean;
  /** Lower case, for the controls' accessible names: "scheduled job", "API", "AI agent". */
  entityLabel: string;
  onStartRename?: () => void;
  onConfirmRename?: () => void;
  onAbortRename?: () => void;
  editTestId?: string;
  confirmTestId?: string;
  abortTestId?: string;
  className?: string;
}

export const KuikaEditorTitle = ({
  identity,
  trailing,
  isRenaming = false,
  renameField,
  canRename = true,
  canAbortRename = true,
  entityLabel,
  onStartRename,
  onConfirmRename,
  onAbortRename,
  editTestId,
  confirmTestId,
  abortTestId,
  className
}: KuikaEditorTitleProps) => (
  <span className={classNames("kuika-editor-title", { "kuika-editor-title--renaming": isRenaming }, className)}>
    {isRenaming ? renameField : identity}
    {trailing}
    <span className="kuika-editor-title__actions">
      {isRenaming ? (
        <>
          <KuikaButton
            isIcon
            type="tertiary"
            color="neutral"
            size="sm"
            aria-label={`Save ${entityLabel} name`}
            data-testid={confirmTestId}
            onClick={onConfirmRename}
          >
            <KuikaSvg name="check" width={16} height={16} color="var(--blue-6)" />
          </KuikaButton>
          {canAbortRename && (
            <KuikaButton
              isIcon
              type="tertiary"
              color="neutral"
              size="sm"
              aria-label={`Cancel renaming ${entityLabel}`}
              data-testid={abortTestId}
              onClick={onAbortRename}
            >
              <KuikaSvg name="x" width={16} height={16} />
            </KuikaButton>
          )}
        </>
      ) : (
        canRename && (
          <KuikaButton
            isIcon
            type="tertiary"
            color="neutral"
            size="sm"
            aria-label={`Rename ${entityLabel}`}
            data-testid={editTestId}
            onClick={onStartRename}
          >
            <KuikaSvg name="edit" width={16} height={16} />
          </KuikaButton>
        )
      )}
    </span>
  </span>
);

export interface KuikaEditorIdentityProps {
  /** A bare 16px `KuikaSvg`, not the gradient tile — that one is for dialogs. */
  icon: React.ReactNode;
  /** What the glyph IS, said on hover — the switcher chip's `triggerIconTooltip`, for the same bar. */
  iconTooltip?: React.ReactNode;
  name: string;
  nameTestId?: string;
  className?: string;
}

/**
 * The same identity the switcher chip draws, for an editor that has nothing to switch TO:
 * a screen the project has exactly one of (Session Variables, Run Settings), or a record
 * being created before it joins a list.
 */
export const KuikaEditorIdentity = ({ icon, iconTooltip, name, nameTestId, className }: KuikaEditorIdentityProps) => (
  <span className={classNames("kuika-editor-identity", OVERFLOW_REVEAL_HOST_CLASS, className)}>
    {iconTooltip ? (
      <KuikaTooltip content={iconTooltip} position="bottom">
        {/* A real element for the tooltip to hang its hover on: `KuikaSvg` takes no ref. */}
        <span className="kuika-editor-identity__icon">{icon}</span>
      </KuikaTooltip>
    ) : (
      icon
    )}
    <KuikaTextMedium className="kuika-editor-identity__name" data-testid={nameTestId}>
      <KuikaOverflowText>{name}</KuikaOverflowText>
    </KuikaTextMedium>
  </span>
);
